/**
 * src/pages/Projects.jsx — the multi-session project list.
 *
 * Filters by artist, client and status, all server-side. The "sessions" column
 * is the one artists actually scan — "3 of 8" tells them at a glance which
 * sleeves are stalling.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../lib/api.js';
import { useApi } from '../lib/useApi.js';
import { formatDate } from '../lib/format.js';
import { Empty, ErrorBanner, Loader, Modal, PageHeader, Pill } from '../components/ui.jsx';

export default function Projects() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ artistId: '', status: '' });
  const [creating, setCreating] = useState(false);

  const params = new URLSearchParams();
  if (filters.artistId) params.set('artistId', filters.artistId);
  if (filters.status) params.set('status', filters.status);
  const query = `/api/projects${params.toString() ? `?${params}` : ''}`;

  const { data, error, loading, reload } = useApi(query, [filters.artistId, filters.status]);
  const artists = useApi('/api/artists');
  const projects = data?.projects ?? [];

  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Multi-session work — sleeves, back pieces, anything booked across several sittings."
        actions={
          <>
            <select value={filters.artistId} onChange={set('artistId')} aria-label="Filter by artist" style={{ width: 170 }}>
              <option value="">All artists</option>
              {(artists.data?.artists ?? []).map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <select value={filters.status} onChange={set('status')} aria-label="Filter by status" style={{ width: 140 }}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="complete">Complete</option>
            </select>
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              New project
            </button>
          </>
        }
      />

      <div className="card">
        <Loader loading={loading} error={error}>
          {projects.length === 0 ? (
            <Empty>No projects match. Start one from a client who is booking a bigger piece.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Client</th>
                    <th>Artist</th>
                    <th>Sessions</th>
                    <th>Started</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.id} className="clickable" onClick={() => navigate(`/projects/${p.id}`)}>
                      <td>
                        <Link to={`/projects/${p.id}`}>{p.title}</Link>
                        {p.bodyLocation ? <div className="muted small">{p.bodyLocation}</div> : null}
                      </td>
                      <td className="muted">{p.clientName}</td>
                      <td className="muted">{p.artistName}</td>
                      <td className="mono">
                        {p.sessionCount}
                        {p.estimatedSessions ? ` of ${p.estimatedSessions}` : ''}
                      </td>
                      <td className="muted mono">{formatDate(p.createdAt)}</td>
                      <td><Pill status={p.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Loader>
      </div>

      {creating ? (
        <NewProject
          artists={artists.data?.artists ?? []}
          onClose={() => setCreating(false)}
          onCreated={(project) => navigate(`/projects/${project.id}`)}
          onReload={reload}
        />
      ) : null}
    </>
  );
}

/**
 * New-project form.
 *
 * @param {{ artists: object[], onClose: () => void, onCreated: (p: object) => void, onReload: () => void }} props
 * @returns {JSX.Element}
 */
function NewProject({ artists, onClose, onCreated, onReload }) {
  const clients = useApi('/api/clients');
  const [form, setForm] = useState({
    clientId: '',
    artistId: '',
    title: '',
    bodyLocation: '',
    estimatedSessions: '',
    notes: '',
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/projects', {
        clientId: form.clientId,
        artistId: form.artistId,
        title: form.title,
        bodyLocation: form.bodyLocation || undefined,
        estimatedSessions: form.estimatedSessions ? Number(form.estimatedSessions) : undefined,
        notes: form.notes || undefined,
      });
      onReload();
      onCreated(res.project);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="New project" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>
        <div>
          <label htmlFor="pclient">Client</label>
          <select id="pclient" value={form.clientId} onChange={set('clientId')} required>
            <option value="">Choose a client…</option>
            {(clients.data?.clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="partist">Artist</label>
          <select id="partist" value={form.artistId} onChange={set('artistId')} required>
            <option value="">Choose an artist…</option>
            {artists.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ptitle">Title</label>
          <input
            id="ptitle"
            value={form.title}
            onChange={set('title')}
            placeholder="Left arm sleeve — koi / lotus"
            required
          />
        </div>
        <div className="row">
          <div className="grow">
            <label htmlFor="plocation">Body location</label>
            <input
              id="plocation"
              value={form.bodyLocation}
              onChange={set('bodyLocation')}
              placeholder="left arm, inner forearm"
            />
          </div>
          <div style={{ width: 150 }}>
            <label htmlFor="psessions">Est. sessions</label>
            <input
              id="psessions"
              type="number"
              min="1"
              value={form.estimatedSessions}
              onChange={set('estimatedSessions')}
            />
          </div>
        </div>
        <div>
          <label htmlFor="pnotes">Notes</label>
          <textarea id="pnotes" value={form.notes} onChange={set('notes')} />
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create project'}
        </button>
      </form>
    </Modal>
  );
}
